import { apiClient } from '../api/client';

/**
 * Sdílená nastavení instance — šablony smluv a protokolů, číselníky a lhůty
 * varování.
 *
 * Proč to existuje: dřív se tyhle věci ukládaly do localStorage prohlížeče.
 * Znamenalo to, že si každý uživatel a každý počítač nesl vlastní verzi kupní
 * smlouvy a vlastní seznam způsobů platby. U právního dokumentu je to
 * nepřijatelné — musí platit pro celou firmu.
 *
 * Jak to funguje: hodnoty žijí na serveru v tabulce `app_settings`. Po
 * přihlášení se všechny načtou najednou do paměti a zrcadlí se do
 * localStorage. Díky tomu můžou desítky míst v aplikaci číst nastavení
 * synchronně (generátory dokumentů to potřebují) a zároveň systém funguje
 * i při krátkém výpadku API.
 */

/** Klíče, které patří celé firmě. Ostatní zůstávají v prohlížeči. */
const SHARED_PREFIXES = ['tmpl_', 'theme_', 'ad_tmpl_', 'custom_feat_', 'custom_tech_', 'sellersig_', 'signersig_', 'themecfg_'];
const SHARED_EXACT = new Set([
  'app_theme_id',
  'ui_cfg',
  'payment_methods_v1',
  'custom_customer_fields',
  'vehicle_tech_fields',
  'reservation_warning_days',
  'stk_warning_days',
  'invoice_history_after_months',
]);

export function isSharedKey(key: string): boolean {
  return SHARED_EXACT.has(key) || SHARED_PREFIXES.some(p => key.startsWith(p));
}

let cache: Record<string, string> = {};
let loaded = false;

/** Bezpečné čtení z localStorage — v privátním okně může vyhodit výjimku. */
function lsGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* plné nebo zakázané úložiště */ }
}
function lsDel(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/**
 * Načte sdílená nastavení ze serveru. Volá se jednou po přihlášení.
 *
 * Zároveň provede jednorázovou migraci: co má uživatel uložené lokálně a na
 * serveru to ještě není, se nahraje nahoru. Nikdo tak nepřijde o šablony,
 * které si upravil dřív, než tahle změna vznikla.
 */
export async function loadSharedSettings(): Promise<void> {
  try {
    const { data } = await apiClient.get('/app-settings');
    const server: Record<string, string> = data?.data ?? {};
    cache = { ...server };

    // Migrace lokálních hodnot, které server ještě nezná.
    const toPush: Array<[string, string]> = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !isSharedKey(key) || key in server) continue;
        const val = lsGet(key);
        if (val != null && val !== '') toPush.push([key, val]);
      }
    } catch { /* localStorage nedostupné */ }

    for (const [key, val] of toPush) {
      try {
        await apiClient.put(`/app-settings/${encodeURIComponent(key)}`, { value: val });
        cache[key] = val;
      } catch {
        // Nemá právo zapisovat (prodejce, účetní) nebo výpadek — lokální
        // hodnota zůstane v cache, ať se mu nerozbije práce.
        cache[key] = val;
      }
    }

    // Zrcadlo do prohlížeče kvůli odolnosti proti výpadku API.
    for (const [key, val] of Object.entries(cache)) lsSet(key, val);
    loaded = true;
  } catch {
    // Server nedostupný — jedeme z toho, co je v prohlížeči.
    loaded = false;
  }
}

/** Synchronní čtení. Před načtením ze serveru vrací hodnotu z prohlížeče. */
export function getShared(key: string): string | null {
  if (loaded && key in cache) return cache[key];
  const local = lsGet(key);
  if (local != null) return local;
  return key in cache ? cache[key] : null;
}

/** Zápis. Uloží se na server pro celou firmu; lokální kopie je jen záloha. */
export async function setShared(key: string, value: string): Promise<void> {
  cache[key] = value;
  lsSet(key, value);
  await apiClient.put(`/app-settings/${encodeURIComponent(key)}`, { value });
}

/** Zápis bez čekání — pro místa, kde na výsledku nezáleží. */
export function setSharedNoWait(key: string, value: string): void {
  void setShared(key, value).catch(() => { /* zůstane aspoň lokálně */ });
}

/** Smazání (typicky „Obnovit výchozí šablonu"). */
export async function removeShared(key: string): Promise<void> {
  delete cache[key];
  lsDel(key);
  await apiClient.delete(`/app-settings/${encodeURIComponent(key)}`);
}

/** Jsou nastavení načtená ze serveru? Pro odlišení výpadku od prázdné instance. */
export function sharedSettingsLoaded(): boolean {
  return loaded;
}
